import React, { useCallback, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import useFeatureRedirect from "../../../hooks/useFeatureRedirect";
import usePlanLimitRedirect from "../../../hooks/usePlanLimitRedirect";
import useAccountSetupRedirect from "../../../hooks/useAccountSetupRedirect";
import { FEATURE_IDS } from "../../../constants/featureAccess";
import { FRONTEND_BASE_URL, notification } from "../../../utils/utility";

import useBuilderBreakpoint from "./hooks/useBuilderBreakpoint";
import { BuilderProvider, useBuilder } from "./state/builderContext";
import { COPY, DEFAULT_COLOR, KIND, ROUTES } from "./state/constants";
import BuilderTopbar from "./shell/BuilderTopbar";
import BuilderSidebar from "./shell/BuilderSidebar";
import BuilderFooter from "./shell/BuilderFooter";
import BuilderToast, { BuilderConfirm } from "./shell/BuilderToast";
import PreviewOverlay from "./shell/PreviewOverlay";
import SetupRequirements from "./shell/SetupRequirements";
import SectionRouter from "./sections/SectionRouter";
import { copyText } from "./success/EventSuccess";
import "./styles/event-builder.scss";

/**
 * Event Builder — route entry (plan 3.1 / 3.2 / 8.1).
 *
 *   /admin/events/builder        new event   (?type=free-event|paid-event, ?section=)
 *   /admin/events/builder/:id    edit/continue event `id`
 *
 * The four guards are the same hooks `AddNewEventGuarded` uses today (8.1). They live inside the
 * provider so the paid guard can read the loaded `is_event_paid` for edits.
 */
const BuilderGuards = ({ isEdit }) => {
  const { state } = useBuilder();

  // New event: kind from ?type (state.meta.kind). Edit: from the loaded `is_event_paid` (null until loaded).
  const isPaidEvent = isEdit ? state.lookups.loadedIsPaid === true : state.meta.kind === KIND.PAID;

  useFeatureRedirect(FEATURE_IDS.COLLECT_PAYMENTS, ROUTES.PICKER, isPaidEvent);
  usePlanLimitRedirect("events", ROUTES.PICKER, !isEdit);
  useAccountSetupRedirect(!isEdit, { bounceToList: true });
  return null;
};

const LoadState = ({ error }) => {
  const navigate = useNavigate();
  if (!error) {
    return (
      <div className="eb-loading" role="status" aria-live="polite">
        <div className="eb-spinner" />
        Loading event…
      </div>
    );
  }
  return (
    <div className="eb-loading" role="alert">
      <p>{error === "not-found" ? "This event could not be found." : "The event could not be loaded. Please try again."}</p>
      <button type="button" className="eb-outline-small" onClick={() => navigate(ROUTES.LIST)}>
        Back to events
      </button>
    </div>
  );
};

const EventBuilderShell = () => {
  const rootRef = useRef(null);
  const bp = useBuilderBreakpoint(rootRef);
  const navigate = useNavigate();
  const { state, publish, businessId } = useBuilder();
  const [publishing, setPublishing] = useState(false);

  const onPublish = useCallback(async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const result = await publish();
      if (!result || !result.ok) return;
      const slug = state.type.event_url;
      if (slug && businessId) {
        const copied = await copyText(`${FRONTEND_BASE_URL}booking/${businessId}/${slug}`);
        if (copied) notification.success(COPY.toast.linkCopied);
      }
      navigate(ROUTES.LIST);
    } finally {
      setPublishing(false);
    }
  }, [publish, publishing, state.type.event_url, businessId, navigate]);

  const accent = (state.details && state.details.event_color) || DEFAULT_COLOR;

  return (
    <main ref={rootRef} className="eb-root eb-builder-shell" data-eb-bp="wide" style={{ "--event-accent": accent }}>
      <BuilderTopbar onPublish={onPublish} publishing={publishing} />
      <div className="eb-builder-body">
        <BuilderSidebar bp={bp} />
        <main className="eb-builder-main">
          {state.meta.loading || state.meta.loadError ? <LoadState error={state.meta.loadError} /> : <SectionRouter />}
        </main>
      </div>
      {!state.meta.loadError ? <BuilderFooter onPublish={onPublish} publishing={publishing} /> : null}
      <PreviewOverlay />
      <SetupRequirements />
      <BuilderConfirm />
      <BuilderToast />
    </main>
  );
};

const EventBuilderPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  return (
    // key: a real change of event id remounts; the /builder → /builder/:id rewrite after the
    // first save is recovered through the provider's state handoff (3.4 rule 1)
    <BuilderProvider key={id || "new"} eventId={id}>
      <BuilderGuards isEdit={isEdit} />
      <EventBuilderShell />
    </BuilderProvider>
  );
};

export default EventBuilderPage;
